import { useEffect, useState, type FormEvent } from 'react';
import type { CreateEstimateInput, UpdateEstimateInput } from '@armard/shared';
import { formatCurrency } from '@armard/shared';
import { Button, FormSaveBar, GlassPanel, Input, PageHeader, Select } from '../ui';
import { useCustomer, type Customer } from '../../hooks/useCustomers';
import { CustomerSelect } from '../invoices/CustomerSelect';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import type { Estimate } from '../../hooks/useEstimates';

type ItemDraft = {
  key: string;
  description: string;
  longDescription: string;
  quantity: number;
  unit: string;
  unitPrice: number;
};

function newItem(): ItemDraft {
  return {
    key: crypto.randomUUID(),
    description: '',
    longDescription: '',
    quantity: 1,
    unit: 'Stück',
    unitPrice: 0,
  };
}

interface EstimateFormProps {
  initial?: Estimate;
  initialCustomerId?: string;
  title: string;
  submitLabel: string;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (values: CreateEstimateInput | UpdateEstimateInput) => Promise<void>;
}

export function EstimateForm({
  initial,
  initialCustomerId,
  title,
  submitLabel,
  isSaving,
  onCancel,
  onSave,
}: EstimateFormProps) {
  const customerIdSeed = initial?.customerId ?? initialCustomerId ?? '';
  const { data: selectedCustomer } = useCustomer(customerIdSeed);
  const [customerId, setCustomerId] = useState(customerIdSeed);
  const [selectedCustomerValue, setSelectedCustomerValue] = useState<Customer | null>(null);
  useEffect(() => {
    if (selectedCustomer?.id === customerId) setSelectedCustomerValue(selectedCustomer);
  }, [customerId, selectedCustomer]);
  const [issueDate, setIssueDate] = useState(
    initial?.issueDate ?? new Date().toISOString().slice(0, 10)
  );
  const [validUntil, setValidUntil] = useState(initial?.validUntil ?? '');
  const [subject, setSubject] = useState(initial?.subject ?? '');
  const [customerReference, setCustomerReference] = useState(initial?.customerReference ?? '');
  const [introText, setIntroText] = useState(initial?.introText ?? '');
  const [closingText, setClosingText] = useState(initial?.closingText ?? '');
  const [vatRate, setVatRate] = useState(Number(initial?.vatRate ?? 19));
  const [items, setItems] = useState<ItemDraft[]>(
    initial?.items?.map((item) => ({
      key: item.id || crypto.randomUUID(),
      description: item.description,
      longDescription: item.longDescription ?? '',
      quantity: Number(item.quantity),
      unit: item.unit ?? 'Stück',
      unitPrice: Number(item.unitPrice),
    })) ?? [newItem()]
  );
  const [editVersion] = useState(initial?.version);
  const [formError, setFormError] = useState('');
  useUnsavedChanges({
    customerId,
    issueDate,
    validUntil,
    subject,
    customerReference,
    introText,
    closingText,
    vatRate,
    items,
  });

  const setItem = (key: string, field: keyof Omit<ItemDraft, 'key'>, value: string | number) =>
    setItems((current) =>
      current.map((item) => (item.key === key ? { ...item, [field]: value } : item))
    );
  const net = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaving) return;
    setFormError('');
    if (!customerId) return setFormError('Bitte wählen Sie einen Kunden.');
    if (items.some((item) => !item.description.trim()))
      return setFormError('Bitte beschreiben Sie jede Position.');
    if (items.some((item) => !Number.isFinite(item.quantity) || item.quantity <= 0))
      return setFormError('Die Menge jeder Position muss größer als 0 sein.');
    if (items.some((item) => !Number.isFinite(item.unitPrice) || item.unitPrice < 0))
      return setFormError('Der Preis darf nicht negativ sein.');

    const common = {
      customerId,
      issueDate,
      validUntil: validUntil || undefined,
      subject: subject || undefined,
      customerReference: customerReference || undefined,
      introText: introText || undefined,
      closingText: closingText || undefined,
      vatRate,
      items: items.map((item, index) => ({
        position: index + 1,
        description: item.description.trim(),
        longDescription: item.longDescription || undefined,
        quantity: item.quantity,
        unit: item.unit.trim() || 'Stück',
        unitPrice: item.unitPrice,
      })),
    };
    await onSave(
      initial
        ? {
            ...common,
            validUntil: validUntil || null,
            subject: subject || null,
            customerReference: customerReference || null,
            introText: introText || null,
            closingText: closingText || null,
            version: editVersion,
          }
        : common
    );
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      <PageHeader
        backTo="/estimates"
        backLabel="Angebote"
        title={title}
        subtitle={initial?.estimateNumber}
      />
      <fieldset
        disabled={isSaving}
        aria-busy={isSaving}
        className="min-w-0 border-0 p-0 m-0 space-y-6"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Grunddaten</h2>
              <div className="space-y-4">
                <CustomerSelect
                  value={customerId}
                  selectedCustomer={selectedCustomerValue}
                  onChange={(id, customer) => {
                    setCustomerId(id);
                    setSelectedCustomerValue(customer);
                  }}
                />
                <Input
                  name="estimate-subject"
                  label="Betreff"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
                <Input
                  name="estimate-reference"
                  label="Kundenreferenz"
                  value={customerReference}
                  onChange={(e) => setCustomerReference(e.target.value)}
                />
                <label className="block text-sm text-white/60">
                  Einleitungstext
                  <textarea
                    value={introText}
                    onChange={(e) => setIntroText(e.target.value)}
                    rows={3}
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white"
                  />
                </label>
              </div>
            </GlassPanel>

            <GlassPanel>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-white">Positionen</h2>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setItems((current) => [...current, newItem()])}
                >
                  + Position
                </Button>
              </div>
              <div className="space-y-4">
                {items.map((item, index) => (
                  <div key={item.key} className="p-4 bg-white/5 rounded-lg space-y-3">
                    <div className="flex justify-between text-sm text-white/50">
                      <span>Position {index + 1}</span>
                      {items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setItems((current) => current.filter((row) => row.key !== item.key))
                          }
                        >
                          Entfernen
                        </Button>
                      )}
                    </div>
                    <Input
                      name={`estimate-item-${index}-description`}
                      label="Beschreibung *"
                      value={item.description}
                      onChange={(e) => setItem(item.key, 'description', e.target.value)}
                      required
                    />
                    <Input
                      name={`estimate-item-${index}-long-description`}
                      label="Zusatztext"
                      value={item.longDescription}
                      onChange={(e) => setItem(item.key, 'longDescription', e.target.value)}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Input
                        name={`estimate-item-${index}-quantity`}
                        label="Menge *"
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={item.quantity}
                        onChange={(e) => setItem(item.key, 'quantity', Number(e.target.value))}
                        required
                      />
                      <Input
                        name={`estimate-item-${index}-unit`}
                        label="Einheit"
                        maxLength={20}
                        value={item.unit}
                        onChange={(e) => setItem(item.key, 'unit', e.target.value)}
                        hint="Stunden, Tage, Stück, pauschal oder km für die Rechnungsumwandlung"
                      />
                      <Input
                        name={`estimate-item-${index}-price`}
                        label="Einzelpreis netto"
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unitPrice}
                        onChange={(e) => setItem(item.key, 'unitPrice', Number(e.target.value))}
                        required
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-white/10 space-y-1 text-right">
                <p className="text-white/60">Netto: {formatCurrency(net)}</p>
                <p className="text-white/60">
                  MwSt. ({vatRate}%): {formatCurrency((net * vatRate) / 100)}
                </p>
                <p className="text-lg font-semibold text-white">
                  Gesamt: {formatCurrency(net * (1 + vatRate / 100))}
                </p>
              </div>
            </GlassPanel>
            <GlassPanel>
              <label className="block text-sm text-white/60">
                Schlusstext
                <textarea
                  value={closingText}
                  onChange={(e) => setClosingText(e.target.value)}
                  rows={3}
                  className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white"
                />
              </label>
            </GlassPanel>
          </div>
          <div className="space-y-6">
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Datum und Mehrwertsteuer</h2>
              <div className="space-y-4">
                <Input
                  name="estimate-issue-date"
                  label="Erstelldatum *"
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                  required
                />
                <Input
                  name="estimate-valid-until"
                  label="Gültig bis"
                  type="date"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  hint="Leer = unbefristet"
                />
                <Select
                  name="estimate-vat"
                  label="Mehrwertsteuer"
                  value={String(vatRate)}
                  onChange={(e) => setVatRate(Number(e.target.value))}
                  options={[
                    { value: '19', label: '19%' },
                    { value: '7', label: '7%' },
                    { value: '0', label: '0%' },
                  ]}
                />
              </div>
            </GlassPanel>
          </div>
        </div>
        {formError && (
          <p role="alert" className="text-sm text-red-400">
            {formError}
          </p>
        )}
        <FormSaveBar>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? 'Speichern...' : submitLabel}
          </Button>
        </FormSaveBar>
      </fieldset>
    </form>
  );
}
